import * as React from 'react';
import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from './utils';
import { motion, AnimatePresence } from 'framer-motion';

interface ExpandableScreenContextValue {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  layoutId?: string;
}

const ExpandableScreenContext = createContext<ExpandableScreenContextValue | null>(null);

/**
 * Reference-counted body scroll lock, shared across every open screen.
 *
 * Only the first locker records the page's real overflow values, and only the
 * last release restores them — so nested or overlapping screens can never
 * restore each other's 'hidden' and strand the page unscrollable.
 */
let scrollLockCount = 0;
let prevBodyOverflow = '';
let prevHtmlOverflow = '';
const BODY_PIN_PROPS = ['position', 'top', 'left', 'right', 'width'] as const;
let prevBodyPin: Record<(typeof BODY_PIN_PROPS)[number], string> | null = null;
let lockedScrollY = 0;

/**
 * The body is pinned with position: fixed, not just overflow: hidden. On
 * iPhone, overflow: hidden does not stop Safari from scrolling the page to
 * lift a focused field above the keyboard; the fixed screen stays put while
 * the caret is drawn where the page scrolled to — outside the field. A
 * pinned body cannot scroll, so the caret stays in the box. The page's
 * scroll position is restored on release.
 */
function acquireScrollLock() {
  if (scrollLockCount === 0) {
    prevBodyOverflow = document.body.style.overflow;
    prevHtmlOverflow = document.documentElement.style.overflow;
    lockedScrollY = window.scrollY;
    const style = document.body.style;
    prevBodyPin = { position: style.position, top: style.top, left: style.left, right: style.right, width: style.width };
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    style.position = 'fixed';
    style.top = `-${lockedScrollY}px`;
    style.left = '0';
    style.right = '0';
    style.width = '100%';
  }
  scrollLockCount += 1;
}

function releaseScrollLock() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) {
    document.body.style.overflow = prevBodyOverflow;
    document.documentElement.style.overflow = prevHtmlOverflow;
    if (prevBodyPin) {
      for (const prop of BODY_PIN_PROPS) document.body.style[prop] = prevBodyPin[prop];
      prevBodyPin = null;
    }
    window.scrollTo(0, lockedScrollY);
  }
}

function useExpandableScreen() {
  const context = useContext(ExpandableScreenContext);
  if (!context) {
    throw new Error('ExpandableScreen components must be used within ExpandableScreen');
  }
  return context;
}

interface ExpandableScreenProps {
  children: React.ReactNode;
  layoutId?: string;
  triggerRadius?: string;
  contentRadius?: string;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ExpandableScreen({
  children,
  layoutId,
  triggerRadius,
  contentRadius,
  isOpen: controlledIsOpen,
  onOpenChange,
}: ExpandableScreenProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const setIsOpen = (open: boolean) => {
    if (isControlled) {
      onOpenChange?.(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  const screenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (!isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }

    // Lock scroll while open. documentElement (<html>) is the actual scrolling
    // root in standards mode — locking body alone leaves it scrollable, which on
    // mobile Safari lets touches land on background content behind this
    // fixed-position overlay (taps appear to do nothing because they hit
    // whatever scrolled into that screen position).
    //
    // Screens can nest (an invoice detail opening a PDF viewer, the CRM list
    // opening a client editor). Each instance saving and restoring the overflow
    // it happened to observe would let an inner screen capture the outer
    // screen's 'hidden' and restore it on close, leaving the page permanently
    // unscrollable. So the original value is captured once by the first locker
    // and only restored when the last one releases.
    acquireScrollLock();
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      releaseScrollLock();
    };
  }, [isOpen]);

  // Separate trigger and content from children
  const trigger = React.Children.toArray(children).find(
    (child: any) => child?.type === ExpandableScreenTrigger
  );
  const content = React.Children.toArray(children).find(
    (child: any) => child?.type === ExpandableScreenContent
  );

  const value: ExpandableScreenContextValue = {
    isOpen,
    setIsOpen,
    layoutId,
  };

  return (
    <ExpandableScreenContext.Provider value={value}>
      {trigger}
      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Expanded Screen — sits above nav (z-[999]) */}
              <motion.div
                ref={screenRef}
                layoutId={layoutId}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className={cn(
                  'fixed inset-0 z-[99999]',
                  'bg-black',
                  'flex flex-col'
                )}
                style={{ borderRadius: contentRadius }}
              >
                {/* Close Button */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-6 right-6 z-10 p-2 hover:bg-white/10 transition-all duration-300 ease-out rounded-none group"
                  aria-label="Close"
                >
                  <XMarkIcon className="w-6 h-6 text-white group-hover:scale-110 transition-transform duration-300" />
                </button>

                {/* Content */}
                {content}
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </ExpandableScreenContext.Provider>
  );
}

interface ExpandableScreenTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
}

export function ExpandableScreenTrigger({
  children,
  className,
  onClick,
  ...props
}: ExpandableScreenTriggerProps) {
  const { setIsOpen, layoutId } = useExpandableScreen();

  return (
    <motion.button
      layoutId={layoutId}
      onClick={(e) => {
        setIsOpen(true);
        onClick?.(e as any);
      }}
      className={cn('cursor-pointer', className)}
      {...(props as any)}
    >
      {children}
    </motion.button>
  );
}

interface ExpandableScreenContentProps {
  children: React.ReactNode;
  className?: string;
}

export function ExpandableScreenContent({
  children,
  className,
}: ExpandableScreenContentProps) {
  return (
    <div className={cn('flex-1 flex flex-col overflow-hidden', className)}>
      {children}
    </div>
  );
}
